/**
 * 管理后台页面注册表：侧栏分组、路径、图标、组件的唯一来源。
 *
 * 背景：创始人 2026-11-03 要求管理后台几乎 1:1 复刻 Alice（gateway_admin.html 的 4 个分组、28 个页面）。
 * 设计意图：App.tsx 只读这张表生成路由与侧栏；每个页面一个文件（pages/alice/<Name>.tsx 或原有 pages/<Name>.tsx），
 *   负责人只改自己的页面文件，不碰本表与 App.tsx；需要增删页面时改这里并同步 docs/Alice 前端复刻清单.md。
 * 关键约束：侧栏文案在 packages/i18n/src/admin.ts 的 A.nav，页面标题用同一个 key，保持中韩英三语。
 */
import {
  IconAlertTriangle,
  IconBooks,
  IconChartBar,
  IconChartDots,
  IconChartLine,
  IconChartPie,
  IconCoin,
  IconCreditCard,
  IconFilter,
  IconGift,
  IconId,
  IconKey,
  IconLayoutDashboard,
  IconListDetails,
  IconMicrophone,
  IconPhoto,
  IconPlugConnected,
  IconReceipt2,
  IconRoute,
  IconRouteAltLeft,
  IconSearch,
  IconShieldCheck,
  IconShieldOff,
  IconSpeakerphone,
  IconStack2,
  IconTicket,
  IconTools,
  IconUsers,
  IconVideo,
} from '@tabler/icons-react'
import type { ComponentType } from 'react'
import { A } from '@ap/i18n/admin'
import { BusinessCockpit } from './pages/alice/BusinessCockpit.tsx'
import { Dashboard } from './pages/Dashboard.tsx'
import { Analytics } from './pages/alice/Analytics.tsx'
import { CostAnalytics } from './pages/alice/CostAnalytics.tsx'
import { CostControls } from './pages/alice/CostControls.tsx'
import { ModelUsage } from './pages/alice/ModelUsage.tsx'
import { Accounts } from './pages/Accounts.tsx'
import { Payments } from './pages/Payments.tsx'
import { Campaign } from './pages/alice/Campaign.tsx'
import { CampaignStats } from './pages/alice/CampaignStats.tsx'
import { FirstRechargeStats } from './pages/alice/FirstRechargeStats.tsx'
import { UsageReward } from './pages/alice/UsageReward.tsx'
import { GiftRedemption } from './pages/alice/GiftRedemption.tsx'
import { Codes } from './pages/alice/Codes.tsx'
import { Channels } from './pages/Channels.tsx'
import { ChannelModels } from './pages/ChannelModels.tsx'
import { Models } from './pages/Models.tsx'
import { AutoRouting } from './pages/alice/AutoRouting.tsx'
import { Capabilities } from './pages/alice/Capabilities.tsx'
import { Queue } from './pages/alice/Queue.tsx'
import { Logs } from './pages/alice/Logs.tsx'
import { Images } from './pages/alice/Images.tsx'
import { Videos } from './pages/alice/Videos.tsx'
import { Audios } from './pages/alice/Audios.tsx'
import { SearchRecords } from './pages/alice/SearchRecords.tsx'
import { Abuse } from './pages/alice/Abuse.tsx'
import { Tickets } from './pages/alice/Tickets.tsx'
import { Fallback } from './pages/Fallback.tsx'
import { Audit } from './pages/Audit.tsx'

export type SectionId = 'biz' | 'users' | 'models' | 'ops'

export interface PageDef {
  id: string
  path: string
  section: SectionId
  icon: ComponentType<{ size?: number }>
  label: (typeof A.nav)[keyof typeof A.nav]
  component: ComponentType
}

export const SECTIONS: Record<SectionId, (typeof A.nav)[keyof typeof A.nav]> = {
  biz: A.nav.groupBiz,
  users: A.nav.groupUsers,
  models: A.nav.groupCatalog,
  ops: A.nav.groupOps,
}

export const PAGES: PageDef[] = [
  { id: 'businessCockpit', path: '/business-cockpit', section: 'biz', icon: IconChartLine, label: A.nav.businessCockpit, component: BusinessCockpit },
  { id: 'overview', path: '/', section: 'biz', icon: IconLayoutDashboard, label: A.nav.overview, component: Dashboard },
  { id: 'analytics', path: '/analytics', section: 'biz', icon: IconChartPie, label: A.nav.analytics, component: Analytics },
  { id: 'costAnalytics', path: '/cost-analytics', section: 'biz', icon: IconCoin, label: A.nav.costAnalytics, component: CostAnalytics },
  { id: 'costControls', path: '/cost-controls', section: 'biz', icon: IconShieldCheck, label: A.nav.costControls, component: CostControls },
  { id: 'modelUsage', path: '/model-usage', section: 'biz', icon: IconChartBar, label: A.nav.modelUsage, component: ModelUsage },
  { id: 'accounts', path: '/accounts', section: 'users', icon: IconUsers, label: A.nav.accounts, component: Accounts },
  { id: 'orders', path: '/payments', section: 'users', icon: IconCreditCard, label: A.nav.orders, component: Payments },
  { id: 'campaign', path: '/campaign', section: 'users', icon: IconSpeakerphone, label: A.nav.campaign, component: Campaign },
  { id: 'campaignStats', path: '/campaign-stats', section: 'users', icon: IconChartDots, label: A.nav.campaignStats, component: CampaignStats },
  { id: 'firstRechargeStats', path: '/first-recharge-stats', section: 'users', icon: IconFilter, label: A.nav.firstRechargeStats, component: FirstRechargeStats },
  { id: 'usageReward', path: '/usage-reward', section: 'users', icon: IconGift, label: A.nav.usageReward, component: UsageReward },
  { id: 'giftRedemption', path: '/gift-redemption', section: 'users', icon: IconTicket, label: A.nav.giftRedemption, component: GiftRedemption },
  { id: 'codes', path: '/codes', section: 'users', icon: IconKey, label: A.nav.codes, component: Codes },
  { id: 'providers', path: '/channels', section: 'models', icon: IconPlugConnected, label: A.nav.providers, component: Channels },
  { id: 'providerModels', path: '/channel-models', section: 'models', icon: IconBooks, label: A.nav.providerModels, component: ChannelModels },
  { id: 'models', path: '/models', section: 'models', icon: IconRoute, label: A.nav.models, component: Models },
  { id: 'autoRouting', path: '/auto-routing', section: 'models', icon: IconRouteAltLeft, label: A.nav.autoRouting, component: AutoRouting },
  { id: 'capabilities', path: '/capabilities', section: 'models', icon: IconTools, label: A.nav.capabilities, component: Capabilities },
  { id: 'queue', path: '/queue', section: 'ops', icon: IconStack2, label: A.nav.queue, component: Queue },
  { id: 'logs', path: '/logs', section: 'ops', icon: IconListDetails, label: A.nav.logs, component: Logs },
  { id: 'images', path: '/images', section: 'ops', icon: IconPhoto, label: A.nav.images, component: Images },
  { id: 'videos', path: '/videos', section: 'ops', icon: IconVideo, label: A.nav.videos, component: Videos },
  { id: 'audios', path: '/audios', section: 'ops', icon: IconMicrophone, label: A.nav.audios, component: Audios },
  { id: 'searchRecords', path: '/search-records', section: 'ops', icon: IconSearch, label: A.nav.searchRecords, component: SearchRecords },
  { id: 'abuse', path: '/abuse', section: 'ops', icon: IconShieldOff, label: A.nav.abuse, component: Abuse },
  { id: 'tickets', path: '/tickets', section: 'ops', icon: IconId, label: A.nav.tickets, component: Tickets },
  { id: 'fallback', path: '/fallback', section: 'ops', icon: IconAlertTriangle, label: A.nav.fallback, component: Fallback },
  { id: 'configAudit', path: '/audit', section: 'ops', icon: IconReceipt2, label: A.nav.configAudit, component: Audit },
]
